import type { CguVersionStateDto } from '@/api/legal-admin-client';

// =============================================================================
// cgu-format.ts — Formatage partagé du registre CGU (dates, états, identités opaques).
// =============================================================================
// WCAG 2.1 AA : les LIBELLÉS texte portent l'information ; toute couleur d'état est DOUBLÉE
// d'un libellé (ST-NFR-03). Les identités OPAQUES (subject OIDC admin / contactId fournisseur)
// n'ont AUCUN endpoint de résolution nom/prénom exposé (invariant n°2 : pas de requête cross-
// schéma serveur) → on DÉGRADE proprement à un identifiant tronqué, jamais une valeur inventée.

/** Formate un horodatage ISO en date/heure locale lisible (FR). Fallback : la chaîne brute. */
export function formatDateTime(iso: string | null): string {
  if (iso === null) {
    return '—';
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' });
}

/** Formate une date ISO (sans heure) locale FR. `—` si null. */
export function formatDate(iso: string | null): string {
  if (iso === null) {
    return '—';
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('fr-FR', { dateStyle: 'medium' });
}

/** Libellé humain d'un état de version (le libellé porte l'info, jamais la couleur seule). */
export function stateLabel(state: CguVersionStateDto): string {
  switch (state) {
    case 'Brouillon':
      return 'Brouillon';
    case 'Publiee':
      return 'Publiée';
    case 'Archivee':
      return 'Archivée';
    default:
      return state;
  }
}

/** Variante de Badge pour un état (renfort couleur UNIQUEMENT — le libellé reste porteur). */
export function stateBadgeVariant(
  state: CguVersionStateDto,
): 'default' | 'secondary' | 'outline' {
  switch (state) {
    case 'Publiee':
      return 'default';
    case 'Brouillon':
      return 'secondary';
    case 'Archivee':
      return 'outline';
    default:
      return 'outline';
  }
}

/**
 * Rend un identifiant OPAQUE (subject OIDC admin / contactId) lisible sans le résoudre :
 * tronque à 8 caractères + « … ». `—` si absent. Le nom/prénom N'EST PAS résolu (aucun
 * endpoint de résolution cross-schéma/cross-tenant — dégradation assumée, à documenter).
 */
export function formatOpaqueId(id: string | null): string {
  if (id === null || id.length === 0) {
    return '—';
  }
  return id.length <= 10 ? id : `${id.slice(0, 8)}…`;
}
